import type { FieldErrors } from '@/core/lib/fieldError'
import type { FieldHints } from '@/core/components/form/FormRenderer'
import type { MassnahmeRow, MassnahmePatch, MassnahmeCreate } from '@/core/data/massnahmen'
import type { FormValues } from '@/core/components/form/formModel'
import type { FieldId } from '@/core/lib/result'

export const MASSNAHME_SELECT_REQUIRED_FIELDS: ReadonlySet<FieldId> = new Set([
  'Umfang.Durchführungsform',
  'Details.Durchführungsform',
  'Ansprechpartner.Ansprechpartner',
])

export const MASSNAHME_CREATE_REQUIRED_FIELDS: ReadonlySet<FieldId> = new Set([
  'Maßnahme.Nummer',
  'Maßnahme.Bezeichnung',
  'Umfang.UE-Anzahl',
  'Umfang.Kosten',
  'Zulassung.Beginn',
  'Zulassung.Ende',
])

const CREATE_NON_NULL_FIELDS = [
  'nummer', 'bezeichnung', 'ansprechpartner', 'durchf_form', 'durchf_ort', 'inhalt',
  'ue_anzahl', 'kosten', 'zeitl_umfang_woche', 'start_zulassung', 'ende_zulassung',
] as const

function isCreatePatch(patch: MassnahmePatch): patch is MassnahmePatch & MassnahmeCreate {
  return CREATE_NON_NULL_FIELDS.every((field) => patch[field] !== null)
}

export const MASSNAHME_CREATE_HINTS: FieldHints = new Map([
  ['Umfang.UE-Anzahl', { tone: 'info', text: 'Bitte geben Sie eine ganze Zahl ein.' }],
  ['Umfang.Kosten', { tone: 'info', text: 'Bitte geben Sie eine Zahl mit Punkt als Dezimaltrennzeichen ein, zum Beispiel 12.50.' }],
])

export function checkMassnahmeCreate(values: FormValues): { fields: MassnahmeCreate | null; errors: FieldErrors } {
  const patch = patchFromValues(values)
  const errors = new Map<string, { kind: 'malformed'; expected: 'number' }>()
  if (values.Umfang) {
    const rawUe = str(values.Umfang, 'UE-Anzahl').trim()
    const ue = patch.ue_anzahl
    if (rawUe && (typeof ue !== 'number' || !Number.isInteger(ue) || ue < -2147483648 || ue > 2147483647)) {
      errors.set('Umfang.UE-Anzahl', { kind: 'malformed', expected: 'number' })
    }
    const kosten = str(values.Umfang, 'Kosten').trim()
    if (kosten && !/^[+-]?(?:\p{Nd}+(?:\.\p{Nd}*)?|\.\p{Nd}+)(?:e[+-]?\p{Nd}+)?$/iu.test(kosten.replaceAll('_', ''))) {
      errors.set('Umfang.Kosten', { kind: 'malformed', expected: 'number' })
    }
  }
  const ready = !!patch.nummer && !!patch.bezeichnung && !!patch.ansprechpartner && !!patch.durchf_form
    && (!values.Umfang || (!!str(values.Umfang, 'UE-Anzahl').trim() && !!patch.kosten))
    && (!values.Zulassung || (!!patch.start_zulassung && !!patch.ende_zulassung))
  return { fields: ready && errors.size === 0 && isCreatePatch(patch) ? patch : null, errors }
}

function orNull(v: unknown): string | null {
  const s = typeof v === 'string' ? v.trim() : ''
  return s === '' ? null : s
}

function str(section: Record<string, unknown> | undefined, key: string): string {
  const v = section?.[key]
  return typeof v === 'string' ? v : ''
}

export function valuesForMassnahme(m: MassnahmeRow): Record<string, unknown> {
  return {
    'Maßnahme': { Nummer: m.nummer ?? '', Bezeichnung: m.bezeichnung ?? '' },
    Umfang: {
      'UE-Anzahl': m.ue_anzahl != null ? String(m.ue_anzahl) : '',
      Kosten: m.kosten ?? '',
      'Zeitlicher Umfang pro Woche': m.zeitl_umfang_woche ?? '',
      Durchführungsort: m.durchf_ort ?? '',
      Durchführungsform: m.durchf_form ?? '',
    },
    Zulassung: { Beginn: m.start_zulassung ?? '', Ende: m.ende_zulassung ?? '' },
    Inhalte: { Inhalt: m.inhalt ?? '', 'Ziele und Inhalte': m.ziele_und_inhalt ?? '', Abschluss: m.abschluss ?? '' },

    Details: { Inhalt: m.inhalt ?? '', Durchführungsort: m.durchf_ort ?? '', Durchführungsform: m.durchf_form ?? '' },
    Ansprechpartner: { Ansprechpartner: m.ansprechpartner ?? '' },
    Anwesenheit: { Stichzeit: m.anwesenheit_stichtag ?? '' },
  }
}

export function patchFromValues(values: FormValues): MassnahmePatch {
  const patch: MassnahmePatch = {
    nummer: str(values['Maßnahme'], 'Nummer').trim(),
    bezeichnung: str(values['Maßnahme'], 'Bezeichnung').trim(),
    ansprechpartner: orNull(values.Ansprechpartner?.Ansprechpartner),
  }
  if (values.Umfang) {
    const ueRaw = str(values.Umfang, 'UE-Anzahl').trim()
    const ue = ueRaw === '' ? null : Number(ueRaw)
    patch.ue_anzahl = ue != null && Number.isFinite(ue) ? ue : null
    patch.kosten = orNull(values.Umfang.Kosten)
    patch.zeitl_umfang_woche = str(values.Umfang, 'Zeitlicher Umfang pro Woche').trim()
    patch.durchf_ort = str(values.Umfang, 'Durchführungsort').trim()
    patch.durchf_form = str(values.Umfang, 'Durchführungsform').trim()
  }
  if (values.Zulassung) {
    patch.start_zulassung = orNull(values.Zulassung.Beginn)
    patch.ende_zulassung = orNull(values.Zulassung.Ende)
  }
  if (values.Inhalte) {
    patch.inhalt = str(values.Inhalte, 'Inhalt').trim()
    patch.ziele_und_inhalt = orNull(values.Inhalte['Ziele und Inhalte'])
    patch.abschluss = str(values.Inhalte, 'Abschluss').trim()
  }
  if (values.Details) {
    patch.inhalt = str(values.Details, 'Inhalt').trim()
    patch.durchf_ort = str(values.Details, 'Durchführungsort').trim()
    patch.durchf_form = str(values.Details, 'Durchführungsform').trim()
  }
  const stichzeit = str(values.Anwesenheit, 'Stichzeit')
  if (stichzeit) patch.anwesenheit_stichtag = stichzeit
  return patch
}
